import { createAsyncThunk } from "@reduxjs/toolkit";

import * as productsAPI from "../../../api/products";
import { IProduct, IVerboseProductClass } from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import { markPerfTiming } from "../../../utils/performance";
import { DISCONTINUED } from "../constants";
import {
    ICardDrawerSettings,
    IPLCOptionPanel,
    IPLCProductCategorySelector,
} from "../models";

/**
 * Filter out the variants that are out of stock and discontinued
 */
export const removeDiscontinuedProducts = (products: readonly IProduct[]) => {
    return products.map((product: IProduct) => {
        const filteredChildren = product.children.filter((child: IProduct) => {
            if (!child.availability.is_available_to_buy) {
                return child.availability.code !== DISCONTINUED;
            }
            return true;
        });
        return {
            ...product,
            children: filteredChildren,
        };
    });
};

/**
 * Parameters for loading products in the configurator.
 */
interface LoadProductsParams {
    productIDs: ReadonlyArray<IProductID>;
    initialVariantID?: IProductID | null;
    categorySelectors?: readonly IPLCProductCategorySelector[];
    optionPanels?: readonly IPLCOptionPanel[];
    cardDrawers?: ICardDrawerSettings | null;
}

/**
 * Return type for loadProducts thunk.
 */
interface LoadProductsResult {
    rootProducts: readonly IProduct[];
    productClass: IVerboseProductClass | null;
    initialVariantID?: IProductID | null;
    categorySelectors?: readonly IPLCProductCategorySelector[];
    optionPanels?: readonly IPLCOptionPanel[];
    cardDrawers?: ICardDrawerSettings | null;
}

/**
 * Load products and product class for configurator.
 *
 * Fetches product data for all provided product IDs, filters out discontinued
 * variants, and loads the product class information for the first product.
 * This is the main data loading operation for initializing a configurator.
 *
 * @param {LoadProductsParams} params - The product loading parameters
 * @param {IProductID[]} params.productIDs - Array of product IDs to load
 * @param {IProductID} [params.initialVariantID] - Optional initial variant to select
 * @param {IPLCProductCategorySelector[]} [params.categorySelectors] - Optional category selectors for PLC mode
 * @param {IPLCOptionPanel[]} [params.optionPanels] - Optional option panels for PLC mode
 * @param {ICardDrawerSettings} [params.cardDrawers] - Optional product card drawer settings from the Option Set
 *
 * @returns {Promise<LoadProductsResult>} Promise resolving to products, product class, and config data
 *
 * @example
 * // Load products for a standard configurator
 * dispatch(loadProducts({
 *     productIDs: [123, 456, 789],
 *     initialVariantID: 456
 * }));
 *
 * @example
 * // Load products for a PLC configurator
 * dispatch(loadProducts({
 *     productIDs: [123, 456],
 *     categorySelectors: [...],
 *     optionPanels: [...]
 * }));
 *
 * @sideEffects
 * - Updates `entities.rootProducts` with filtered product data via extraReducers
 * - Updates `entities.productClass` with fetched product class via extraReducers
 * - Updates `entities.categorySelectors` and `entities.optionPanels` via extraReducers
 * - Updates `ui` state with initial variant selection via extraReducers
 * - Logs performance timing to console
 */
export const loadProducts = createAsyncThunk(
    "configurator/loadProducts",
    async (params: LoadProductsParams): Promise<LoadProductsResult> => {
        markPerfTiming("configurator", "loadProducts:start");

        const loading = params.productIDs.map((productID) => {
            const productURL = productsAPI.getProductURL(productID);
            return productsAPI.getProduct(productURL);
        });

        let products = await Promise.all(loading);

        // If a variant is out of stock and discontinued, do not load in the drop-down menu on the configurators.
        products = removeDiscontinuedProducts(products);

        let productClass: IVerboseProductClass | null = null;
        if (products.length > 0) {
            const fetchedProductClass = await productsAPI.getProductClass(
                products[0].product_class_slug,
            );
            if (fetchedProductClass) {
                productClass = fetchedProductClass;
            }
        }

        markPerfTiming("configurator", "loadProducts:done");

        return {
            rootProducts: products,
            productClass: productClass,
            initialVariantID: params.initialVariantID,
            categorySelectors: params.categorySelectors,
            optionPanels: params.optionPanels,
            cardDrawers: params.cardDrawers,
        };
    },
);
